import XRegExp from '@replacements/utils/XRegExp';
import Editor from '../../../context/editor';
import { PhoneticReplacement } from '../../../../server/graphql/__generated.types';

interface PhoneticReplacementArgs {
  input: string;
  phoneticLibrary: Editor.StoreState['phoneticLibrary'];
}

export interface PhoneticReplacementReturnValue {
  inputWithPhoneticReplacement: string;
  phoneticallyReplaced: Editor.StoreState['phoneticallyReplaced'];
}

/*
 * Given the input text and a phonetic replacement library, returns the text with all instances of
 * words found in the library replaced with their phonetic replacement value.
 *
 * Implementation details:
 * For a given replacement {Lorem -> ipsum} we search the input text for case-insensitve instances
 * of 'Lorem'. Each instance will be replaced with the string 'ipsum'. Ex:
 *     [✓] Lorem
 *     [✓] lorem
 *     [✓] LoReM
 *     [✓] Lorem's (becomes ipsum's)
 *     [✓] Lorem-dolor (becomes ipsum-dolor)
 * The following will _not_ result in a string replacement due to the word-boundary constraint:
 *     [x] Loremdolor
 *     [x] DolorLorem
 *
 * @param {PhoneticReplacementReturnValue} args
 * @returns {PhoneticReplacementReturnValue}
 */
export default function phoneticReplacement({
  input,
  phoneticLibrary,
}: PhoneticReplacementArgs): PhoneticReplacementReturnValue {
  let output = input;
  const phoneticallyReplaced: Editor.StoreState['phoneticallyReplaced'] = [];
  phoneticLibrary.forEach((replacement: PhoneticReplacement) => {
    const replaced: Editor.StoreState['phoneticallyReplaced'][0] = {
      instances: 0,
      phoneticReplacement: replacement,
    };
    /** @see replacementRegex for the word-boundary handling */
    output = XRegExp.replaceLbIgnoreRespellings(
      output,
      NO_LOOKBEHIND,
      replacementRegex(replacement.text),
      function handleReplace() {
        replaced.instances += 1;
        return replacement.replacement;
      }
    );
    if (replaced.instances > 0) {
      phoneticallyReplaced.push(replaced);
    }
  });
  return {
    inputWithPhoneticReplacement: output,
    phoneticallyReplaced,
  };
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

/**
 * An always-true lookbehind. The leading word boundary now lives in the regex
 * built by `replacementRegex`, so the `*Lb*` helpers need no lookbehind of
 * their own.
 */
const NO_LOOKBEHIND = '(?<=)';

/**
 * Builds the regex used to find a replacement's text: global, case insensitive,
 * unicode, and constrained so that a replacement only fires on a whole word.
 *
 * Which constraint applies depends on the edge of `text` itself:
 *
 * - a letter edge needs a non-letter next to it, so {Lorem -> ipsum} does not
 *   match inside 'Loremdolor' but still matches 'Lorem-dolor'
 * - a non-letter edge needs whitespace (or the string edge) next to it, so
 *   {/ -> slash} matches the standalone slash in 'he / she' but leaves the
 *   slashes in 'and/or', '24/7' and 'http://x' alone
 *
 * A non-letter edge cannot use the non-letter check: punctuation is ordinarily
 * adjacent to letters, so {/ -> slash} would never match anything at all.
 *
 * Note that `\b` is unusable here as it is not unicode-aware, hence the
 * lookarounds against `\P{L}` (non-letter).
 *
 * @see https://www.regular-expressions.info/unicode.html `\P{L}` = non-unicode-letters
 * @see https://stackoverflow.com/questions/10590098/javascript-regexp-word-boundaries-unicode-characters
 */
export function replacementRegex(text: string): RegExp {
  const isLetter = /\p{L}/u;
  const lead = isLetter.test(text.charAt(0)) ? '(?<=^|\\P{L})' : '(?<=^|\\s)';
  const trail = isLetter.test(text.slice(-1)) ? '(?=\\P{L}|$)' : '(?=\\s|$)';

  return XRegExp(`${lead}(${escapeRegExp(text)})${trail}`, 'giu');
}
